import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import {
  useEffect,
  type FunctionComponent,
  useState,
} from "react";

import { useToast } from "../../../hooks/use-toast";

import {
  findErrorMessage,
  handleHttpError,
} from "../../../utils/utils";

import { Form } from "../../../components/ui/form";
import { FormSelect } from "../../../components/form-components/form-select.components";
import { FormTextArea } from "../../../components/form-components/form-textarea.components";
import LinkOfCreatorComponent from "../../../components/link-of-creator.components";
import {
  zProposalFormAdmin,
  type IzSampleFormAdmin,
} from "../../../z/admin/sample-form.admin.z";

import {
  SocialCreatorTypeOptions,
  SocialPostTypeOptions,
} from "../campaign-form.admin.partials";
import { FormInput } from "../../../components/form-components/form-input.components";
import ShootingStarsBg from "../../../assets/svgs/sales/shooting-stars.svg";
import millify from "millify";
import { useGetBrandsAdminHttp } from "../../../hooks/http/admin/brands/get-brand.admin.http";
import type { IBrand } from "../../../types/models/brands.models.types";
import { useCreateProposalAdminHttp } from "../../../hooks/http/admin/proposals/create-proposals.admin.http";
import { Button } from "../../../components/ui/button";
import { SocialPlatforms } from "../../../types/models/social.models.types";
import Spinner from "../../../components/spinner.components";
import { useGetProposalByIdAdminHttp } from "../../../hooks/http/admin/proposals/get-proposals-by-id.admin.http";
import { useUpdateProposalAdminHttp } from "../../../hooks/http/admin/proposals/update-proposals.admin.http";
import { FormSearchInput } from "../../../components/form-components/form-search-input.components";
import BoostIcon from "../../../assets/svgs/common/boost.svg";

export const CampaignDetailsProposalsAdminPartial: FunctionComponent = () => {
  const { toast } = useToast();
  const { proposalId } = useParams();
  const [brands, setBrands] = useState<IBrand[]>([]);
  const navigate = useNavigate();

  const { execute: createProposalHttp, fetching: creatingProposal } =
    useCreateProposalAdminHttp();
  const { execute: getBrandsHttp, fetching: fetchingBrands } =
    useGetBrandsAdminHttp();
  const { execute: getProposalByIdHttp, fetching: fetchingProposal } =
    useGetProposalByIdAdminHttp();
  const {
    execute: updateProposalHttp,
    fetching: updatingProposal,
  } = useUpdateProposalAdminHttp();

  async function updateProposal(data: IzSampleFormAdmin) {
    if (!proposalId) {
      return;
    }
    console.log("Updating proposal with data:", data);
    const response = await updateProposalHttp({
      body: {
        name: form.formState.dirtyFields.name ? data.name : undefined,
        brief: form.formState.dirtyFields.brief ? data.brief : undefined,
        expectedMetrics:
          form.formState.dirtyFields.expectedMetrics?.cost ||
          form.formState.dirtyFields.expectedMetrics?.views ||
          form.formState.dirtyFields.expectedMetrics?.posts
            ? {
                cost: form.formState.dirtyFields.expectedMetrics?.cost
                  ? Number(data?.expectedMetrics?.cost)
                  : undefined,
                views: form.formState.dirtyFields.expectedMetrics?.views
                  ? Number(data?.expectedMetrics?.views)
                  : undefined,
                posts: form.formState.dirtyFields.expectedMetrics?.posts
                  ? Number(data?.expectedMetrics?.posts)
                  : undefined,
              }
            : undefined,
        referenceCreators:
          data?.referenceCreators?.length > 0 &&
          data.referenceCreators[0].url !== ""
            ? form.formState.dirtyFields.referenceCreators
              ? data.referenceCreators.map((i) => i.url)
              : undefined
            : [],
        notes: form.formState.dirtyFields.notes
          ? data.notes !== null
            ? data.notes
            : undefined
          : undefined,
      },
      pathParams: { proposalId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Proposal updated successfully",
      toastType: "success",
    });
    form.reset(
      {
        brand: response.data.proposal.brand.id,
        name: response.data.proposal.name,
        brief: response.data.proposal.brief,
        socialPostType: response.data.proposal.socialPostType,
        socialCreatorType: response.data.proposal.socialCreatorType,
        expectedMetrics: response.data.proposal.expectedMetrics,
        referenceCreators:
          response.data.proposal.referenceCreators.length > 0
            ? response.data.proposal.referenceCreators.map((i) => ({
                url: i,
              }))
            : [],
        notes: response.data.proposal.notes,
      },
      { keepDirty: false, keepTouched: false }
    );

    navigate(
      `/admin/proposals/proposal/${response.data.proposal.id}/campaign-details`
    );
  }

  async function getProposal() {
    if (!proposalId) {
      return;
    }
    const response = await getProposalByIdHttp({
      body: null,
      pathParams: { proposalId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    form.reset(
      {
        brand: response.data.proposal.brand.id,
        name: response.data.proposal.name,
        brief: response.data.proposal.brief,
        socialPostType: response.data.proposal.socialPostType,
        socialCreatorType: response.data.proposal.socialCreatorType,
        expectedMetrics: response.data.proposal.expectedMetrics,
        referenceCreators:
          response.data.proposal.referenceCreators.length > 0
            ? response.data.proposal.referenceCreators.map((i) => ({
                url: i,
              }))
            : [],
        notes: response.data.proposal.notes,
      },
      { keepDirty: false, keepTouched: false }
    );
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getBrands();
    if (proposalId) {
      getProposal();
    }
  }, [proposalId]);

  async function createProposal(data: IzSampleFormAdmin) {
    console.log(data);
    const response = await createProposalHttp({
      body: {
        brand: data.brand,
        name: data.name,
        brief: data.brief,
        socialPlatform: SocialPlatforms.instagram,
        socialPostType: data.socialPostType,
        socialCreatorType: data.socialCreatorType,
        expectedMetrics: data.expectedMetrics
          ? {
              cost: data.expectedMetrics.cost,
              views: data.expectedMetrics.views,
              posts: data.expectedMetrics.posts,
            }
          : null,
        referenceCreators:
          data?.referenceCreators?.length > 0 &&
          data.referenceCreators[0].url !== ""
            ? data.referenceCreators.map((i) => i.url) || []
            : [],
        notes: data.notes ? data.notes : undefined,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Proposal created successfully",
      toastType: "success",
    });
    navigate(
      `/admin/proposals/proposal/${response.data.proposal.id}/campaign-details`
    );
  }

  async function getBrands() {
    const response = await getBrandsHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setBrands(response.data.brands);
  }

  const form = useForm<IzSampleFormAdmin>({
    resolver: zodResolver(zProposalFormAdmin),
    defaultValues: {
      brand: "",
      name: "",
      brief: "",
      expectedMetrics: {
        cost: 0,
        views: 0,
        posts: 0,
      },
      referenceCreators: [],
      notes: "",
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      console.log(key);
      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  async function onSubmit(data: IzSampleFormAdmin) {
    if (proposalId) {
      return await updateProposal(data);
    }
    await createProposal(data);
  }

  const cost = form.watch("expectedMetrics.cost");
  const views = form.watch("expectedMetrics.views");

  function costPerView() {
    if (views && cost) {
      return cost / views;
    }
    return 0;
  }


  const formfieldDirty = Object.keys(form.formState.dirtyFields).length > 0;

  if (fetchingProposal || fetchingBrands) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col px-6 pl-8 w-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            onError={(formState) => console.log("Form error:", formState)}
            className="relative gap-8 grid grid-cols-2 py-10 w-full"
          >
            <div className="flex gap-8 col-span-2 w-full">
              <FormSearchInput
                form={form}
                name="brand"
                label="Select brand"
                placeholder="Search brand"
                required
                options={brands.map((b) => ({ value: b.id, label: b.name }))}
                showAllOnEmpty
                maxResultsWhileSearching={5}
                disabled={proposalId !== undefined}
              />

              <FormInput
                form={form}
                name="name"
                label="Proposal campaign name"
                placeholder="Enter proposal campaign name"
                required={true}
                type="text"
              />
            </div>
            <div className="flex gap-8 col-span-2 w-full">
              <FormSelect
                form={form}
                name="socialPostType"
                label="Post type"
                placeholder="Select post type"
                required={true}
                options={Object.values(SocialPostTypeOptions)}
                disabled={proposalId !== undefined}
              />
              <FormSelect
                form={form}
                name="socialCreatorType"
                label="Creator type"
                placeholder="Select creator type"
                required={true}
                options={Object.values(SocialCreatorTypeOptions)}
                disabled={proposalId !== undefined}
              />
            </div>

            <div className="col-span-2">
              <FormTextArea
                form={form}
                name="brief"
                label="Campaign brief"
                placeholder="Enter campaign brief here..."
                required
              />
            </div>
            <div className="col-span-2">
              <div className="md:flex items-center hidden bg-tertiarybuttonbg border border-border rounded-xl w-full h-fit overflow-hidden">
                <div className="flex justify-between items-center gap-4 bg-background px-6 py-8 pr-10 w-9/12">
                  <FormInput
                    form={form}
                    name="expectedMetrics.views"
                    label="Expected views"
                    placeholder="Enter expected views"
                    required={false}
                    type="number"
                  />
                  <div className="border-border border-l h-14" />
                  <FormInput
                    form={form}
                    name="expectedMetrics.cost"
                    label="Cost of campaign"
                    placeholder="Enter cost of campaign"
                    required={false}
                    prefix="₹"
                    type="number"
                  />
                  <div className="border-border border-l h-14" />

                  <FormInput
                    form={form}
                    name="expectedMetrics.posts"
                    label="Expected posts"
                    placeholder="Enter expected posts"
                    required={false}
                    type="number"
                  />
                </div>
                <div className="relative flex justify-evenly items-center w-3/12 h-full">
                  {/*  */}
                  <div className="absolute w-full h-full">
                    <ShootingStarsBg />
                  </div>

                  {/*  */}
                  <div className="text-center">
                    <p className="font-semibold text-base text-secondary">
                      Cost per views
                    </p>{" "}
                    <p className="font-semibold text-5xl text-white tracking-tight">
                      ₹ {millify(costPerView())}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <LinkOfCreatorComponent label="Reference creators" form={form} />
            </div>
            <div className="col-span-2">
              <FormTextArea
                form={form}
                name="notes"
                label="Campaign note"
                placeholder="Enter your remarks here ...."
              />
            </div>
          </form>
        </Form>
      </div>
      <Outlet />
      <div className="bottom-0 z-10 sticky flex justify-end items-center bg-sidemenubg px-10 border-t border-border w-full">
 
        <div className="flex justify-end items-center gap-5 py-5 w-full">
          {proposalId ? (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/brands/proposal/${proposalId}`
                );
                toast({
                  content: "Proposal Link Copied",
                  toastType: "success",
                });
              }}
              className="bg-soloboost px-6 py-6 font-semibold text-sm"
            >
              <BoostIcon className="pr-2 w-6 h-6" />
              Share Proposal
            </Button>
          ) : null}
          {proposalId ? (
            <Link to={`/admin/proposals/proposal/${proposalId}/creator-list`}>
              <Button className="bg-tertiorybuttonbg px-10 py-6 border border-border font-semibold">
                Manage creator list{" "}
              </Button>
            </Link>
          ) : null}

          {proposalId ? (
            <Button
              disabled={!formfieldDirty}
              onClick={() => {
                form.handleSubmit(onSubmit)();
              }}
              className="bg-primarybuttonbg py-6 border border-border w-28 font-semibold text-black"
            >
              {updatingProposal ? <Spinner width={15} /> : "Save"}
            </Button>
          ) : (
            <Button
              onClick={() => {
                form.handleSubmit(onSubmit)();
              }}
              className="bg-primarybuttonbg px-10 py-6 border border-border font-semibold text-black"
            >
              {creatingProposal ? <Spinner width={15} /> : "Create proposal"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
